import type { ID } from "@/lib/catalog/types";

export type ProductDealType = "owner_vehicle" | "purchased" | "partnership";
export type ProductProfitRuleType = "owner_surplus" | "commission" | "profit_sharing";
export type DealPartnerProfitType = "percentage" | "fixed";
/** Showroom Charges type — percentage of sale price, percentage of profit, or fixed amount. */
export type ShowroomChargeType = "sale_percentage" | "profit_percentage" | "fixed";

export interface DealPartnerDraft {
  key: string;
  partyId: ID | null;
  /** Display name from API (`party_name`); optional on create forms. */
  partyName?: string | null;
  investmentAmount: number | null;
  investmentRatio: number | null;
  profitType: DealPartnerProfitType | null;
  profitValue: number | null;
  /** Fixed Company Owner / Showroom commission row (not listed under Deal Partners). */
  isCompanyOwner?: boolean;
}

export const COMPANY_OWNER_PARTY_NAME = "Company Owner";
export const COMPANY_OWNER_PARTNER_KEY = "dp-company-owner";

export const DEAL_TYPE_OPTIONS: { value: ProductDealType; label: string }[] = [
  { value: "owner_vehicle", label: "Seller Vehicle" },
  { value: "purchased", label: "Purchased" },
  { value: "partnership", label: "Partnership" },
];

export const PROFIT_RULE_OPTIONS: { value: ProductProfitRuleType; label: string }[] = [
  { value: "owner_surplus", label: "Surplus" },
  { value: "commission", label: "Commission" },
  { value: "profit_sharing", label: "Profit Sharing" },
];

export const DEAL_PARTNER_PROFIT_TYPE_OPTIONS: { value: DealPartnerProfitType; label: string }[] = [
  { value: "fixed", label: "Fixed" },
  { value: "percentage", label: "Percentage" },
];

export const SHOWROOM_CHARGE_TYPE_OPTIONS: { value: ShowroomChargeType; label: string }[] = [
  { value: "profit_percentage", label: "Profit %" },
  { value: "sale_percentage", label: "Sale %" },
  { value: "fixed", label: "Fixed" },
];

export function isShowroomPercentType(
  type: ShowroomChargeType | string | null | undefined,
): type is "sale_percentage" | "profit_percentage" {
  return type === "sale_percentage" || type === "profit_percentage" || type === "percentage";
}

export function normalizeShowroomChargeType(
  raw: unknown,
): ShowroomChargeType | null {
  if (raw === "fixed" || raw === "sale_percentage" || raw === "profit_percentage") {
    return raw;
  }
  // Legacy DB / API value before sale_percentage / profit_percentage split.
  if (raw === "percentage") return "profit_percentage";
  return null;
}

/** Allowed profit rules for a deal type (order = preference). */
export function profitRulesForDealType(dealType: ProductDealType | null | undefined): ProductProfitRuleType[] {
  // Temporarily hide Profit Sharing for Owner Vehicle (not needed yet).
  if (dealType === "owner_vehicle") return ["owner_surplus", "commission" /*, "profit_sharing" */];
  if (dealType === "purchased") return [];
  // Temporarily hide Commission for Partnership (not needed yet).
  if (dealType === "partnership") return ["profit_sharing" /*, "commission" */];
  return [];
}

/** Add Partner is only available for Partnership deals. */
export function allowsAddDealPartner(dealType: ProductDealType | null | undefined): boolean {
  return dealType === "partnership";
}

/** Owner Vehicle / Purchased: exactly one partner row (no add/delete). */
export function isFixedSingleDealPartner(dealType: ProductDealType | null | undefined): boolean {
  return dealType === "owner_vehicle" || dealType === "purchased";
}

export function showsDealPartners(dealType: ProductDealType | null | undefined): boolean {
  return dealType != null;
}

/** Fresh dependent deal fields after Deal Type changes (clears leftover partner/rule data). */
export function clearedDealDependents(dealType: ProductDealType | null): {
  profitRuleType: ProductProfitRuleType | null;
  ownerDemandPrice: null;
  dealPartners: DealPartnerDraft[];
  showroomCharge: ShowroomChargeDraft;
} {
  return {
    profitRuleType: null,
    ownerDemandPrice: null,
    // One empty addable partner when a deal type is selected.
    dealPartners: dealType != null ? [emptyDealPartnerRow()] : [],
    showroomCharge: emptyShowroomCharge(),
  };
}

/** Base purchase cost = sum of partner investment amounts (0 / empty → null). */
export function computeBasePurchaseFromDealPartners(
  partners: DealPartnerDraft[] | undefined,
): number | null {
  const total = investmentAmountTotal(partners ?? []);
  if (!Number.isFinite(total) || total <= 0) return null;
  return Number(total.toFixed(2));
}

/** Default profit rule when deal type is selected/changed. */
export function defaultProfitRuleForDealType(
  dealType: ProductDealType | null | undefined,
): ProductProfitRuleType | null {
  const allowed = profitRulesForDealType(dealType);
  return allowed[0] ?? null;
}

export function resolveProfitRuleForDealType(
  dealType: ProductDealType | null | undefined,
  current: ProductProfitRuleType | null | undefined,
): ProductProfitRuleType | null {
  const allowed = profitRulesForDealType(dealType);
  if (current && allowed.includes(current)) return current;
  return allowed[0] ?? null;
}

export function emptyDealPartnerRow(): DealPartnerDraft {
  return {
    key: `dp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    partyId: null,
    investmentAmount: null,
    investmentRatio: null,
    profitType: "percentage",
    profitValue: null,
    isCompanyOwner: false,
  };
}

export type ShowroomChargeDraft = {
  partyId: string | null;
  partyName?: string | null;
  commissionType: ShowroomChargeType | null;
  commissionValue: number | null;
  commissionAmount: number | null;
};

export function emptyShowroomCharge(
  seed?: Partial<ShowroomChargeDraft>,
): ShowroomChargeDraft {
  return {
    partyId: seed?.partyId ?? null,
    partyName: seed?.partyName ?? null,
    commissionType: seed?.commissionType ?? null,
    commissionValue: seed?.commissionValue ?? null,
    commissionAmount: seed?.commissionAmount ?? null,
  };
}

/** @deprecated Use emptyShowroomCharge — kept for gradual migration. */
export function companyOwnerDealPartnerRow(
  seed?: Partial<Pick<DealPartnerDraft, "profitType" | "profitValue" | "partyId">> & {
    partyId?: string | null;
    partyName?: string | null;
    companyId?: string | null;
    companyName?: string | null;
    commissionAmount?: number | null;
    commissionType?: ShowroomChargeType | null;
  },
): ShowroomChargeDraft {
  const legacyType =
    seed?.commissionType ??
    (seed?.profitType === "fixed"
      ? "fixed"
      : seed?.profitType === "percentage"
        ? "profit_percentage"
        : null);
  return emptyShowroomCharge({
    partyId: seed?.partyId ?? (seed?.companyId != null ? String(seed.companyId) : null),
    partyName: seed?.partyName ?? seed?.companyName ?? null,
    commissionType: legacyType,
    commissionValue: seed?.profitValue ?? null,
    commissionAmount: seed?.commissionAmount ?? null,
  });
}

export function isCompanyOwnerPartner(row: DealPartnerDraft | null | undefined): boolean {
  if (!row) return false;
  // Showroom is stored on showroom_charges / companies — do not hide parties merely named "Company Owner".
  return row.isCompanyOwner === true || row.key === COMPANY_OWNER_PARTNER_KEY;
}

/** Only user-added partners (never Company Owner / showroom). */
export function addableDealPartners(partners: DealPartnerDraft[] | undefined): DealPartnerDraft[] {
  return (partners ?? [])
    .filter((p) => !isCompanyOwnerPartner(p))
    .map((p) => ({
      ...p,
      isCompanyOwner: false,
    }));
}

/** Owner Vehicle + Owner Surplus: no commission-based investment pool split. */
export function isOwnerVehicleSurplusDeal(
  dealType: ProductDealType | null | undefined,
  profitRuleType: ProductProfitRuleType | null | undefined,
): boolean {
  return dealType === "owner_vehicle" && profitRuleType === "owner_surplus";
}

export function shouldAutoDistributePartnerInvestments(
  dealType: ProductDealType | null | undefined,
  profitRuleType: ProductProfitRuleType | null | undefined,
): boolean {
  return !isOwnerVehicleSurplusDeal(dealType, profitRuleType);
}

/**
 * Owner Vehicle + Owner Surplus defaults for deal partners:
 * - Investment Amount defaults to Base Purchase Cost when unset (editable afterward)
 * - Partner profit fields are cleared (surplus settled via Showroom Charges + demand)
 */
export function applyOwnerVehicleSurplusPartnerDefaults(input: {
  partners: DealPartnerDraft[] | undefined;
  basePurchase: number | null | undefined;
  expectedSalePrice?: number | null | undefined;
  ownerDemandPrice?: number | null | undefined;
}): DealPartnerDraft[] {
  const rows = addableDealPartners(input.partners);
  const base = Number(input.basePurchase);
  const defaultInvest =
    Number.isFinite(base) && base > 0 ? Number(base.toFixed(2)) : null;

  const next = rows.map((row) => {
    const existing =
      row.investmentAmount != null && Number.isFinite(Number(row.investmentAmount))
        ? Number(row.investmentAmount)
        : null;
    return {
      ...row,
      // Keep user edits; only fill when empty.
      investmentAmount: existing ?? defaultInvest,
      profitType: null,
      profitValue: null,
    };
  });

  const total = investmentAmountTotal(next);
  const denominator = Number.isFinite(base) && base > 0 ? base : total;
  return next.map((row) => ({
    ...row,
    investmentRatio: calculatedInvestmentRatio(row.investmentAmount, denominator),
  }));
}

export function ownerVehicleSurplusPartnersMatch(
  partners: DealPartnerDraft[] | undefined,
  basePurchase: number | null | undefined,
  expectedSalePrice?: number | null | undefined,
  ownerDemandPrice?: number | null | undefined,
): boolean {
  const expected = applyOwnerVehicleSurplusPartnerDefaults({
    partners,
    basePurchase,
    expectedSalePrice,
    ownerDemandPrice,
  });
  const current = addableDealPartners(partners);
  if (expected.length !== current.length) return false;
  return expected.every((row, i) => {
    const cur = current[i];
    if (!cur || cur.key !== row.key) return false;
    if (cur.profitType != null || cur.profitValue != null) return false;
    // Investment is user-editable — only require it to be filled when a default exists.
    const a = cur.investmentAmount;
    const b = row.investmentAmount;
    if (b != null && (a == null || !Number.isFinite(Number(a)))) return false;
    return true;
  });
}

/** True when Commission Type + Value are set so Showroom Charges should be persisted. */
export function hasShowroomCharge(row: ShowroomChargeDraft | null | undefined): boolean {
  if (!row) return false;
  if (row.commissionType !== "fixed" && !isShowroomPercentType(row.commissionType)) return false;
  return row.commissionValue != null && Number.isFinite(Number(row.commissionValue));
}

/** @deprecated Use hasShowroomCharge */
export function hasCompanyOwnerCharge(row: ShowroomChargeDraft | null | undefined): boolean {
  return hasShowroomCharge(row);
}

/** Preliminary Showroom commission amount from a preview base (recalculated on sale later). */
export function computeShowroomChargeAmount(
  baseAmount: number | null | undefined,
  commission: ShowroomChargeDraft | null | undefined,
): number {
  const base = Number(baseAmount);
  if (!Number.isFinite(base) || base <= 0) return 0;
  if (!hasShowroomCharge(commission)) return 0;
  const value = Number(commission!.commissionValue);
  if (!Number.isFinite(value) || value < 0) return 0;

  if (isShowroomPercentType(commission!.commissionType)) {
    return Number(((base * Math.min(value, 100)) / 100).toFixed(2));
  }
  return Number(Math.min(value, base).toFixed(2));
}

/**
 * Preview base for Showroom Charges on the product form.
 * - sale_percentage → Expected Sale Price
 * - profit_percentage → net profit (sale − landed cost)
 * - fixed / unset → prefer sale when available, else net profit, else base purchase
 */
export function showroomCommissionPreviewBase(input: {
  commissionType?: ShowroomChargeType | null;
  dealType?: ProductDealType | null;
  profitRuleType?: ProductProfitRuleType | null;
  basePurchase?: number | null;
  expectedSalePrice?: number | null;
  netProfit?: number | null;
}): number | null {
  const expectedSale = Number(input.expectedSalePrice);
  const basePurchase = Number(input.basePurchase);
  const netProfit = Number(input.netProfit);
  const saleOk = Number.isFinite(expectedSale) && expectedSale > 0 ? expectedSale : null;
  const profitOk = Number.isFinite(netProfit) && netProfit > 0 ? netProfit : null;
  const purchaseOk = Number.isFinite(basePurchase) && basePurchase > 0 ? basePurchase : null;

  if (input.commissionType === "sale_percentage") {
    return saleOk;
  }
  if (input.commissionType === "profit_percentage") {
    if (profitOk != null) return profitOk;
    if (
      saleOk != null &&
      Number.isFinite(basePurchase) &&
      basePurchase >= 0
    ) {
      const implied = saleOk - basePurchase;
      return implied > 0 ? Number(implied.toFixed(2)) : null;
    }
    return null;
  }

  // Fixed / unset: keep prior deal-type heuristics for a reasonable amount cap preview.
  if (input.dealType === "owner_vehicle" && input.profitRuleType === "commission") {
    return saleOk;
  }
  if (input.dealType === "partnership" && input.profitRuleType === "commission") {
    if (profitOk != null) return profitOk;
    if (saleOk != null && Number.isFinite(basePurchase) && basePurchase >= 0) {
      const implied = saleOk - basePurchase;
      return implied > 0 ? Number(implied.toFixed(2)) : null;
    }
    return null;
  }

  return purchaseOk;
}

/** @deprecated Use computeShowroomChargeAmount */
export function computeCompanyOwnerChargeAmount(
  basePurchase: number | null | undefined,
  commission: ShowroomChargeDraft | null | undefined,
): number {
  return computeShowroomChargeAmount(basePurchase, commission);
}

/**
 * Investment pool for Deal Partners = Base Purchase Cost.
 * Showroom / Company Owner charges do not reduce partner investment amounts.
 * Returns null when base is unset, or for Owner Vehicle + Owner Surplus (manual investment).
 */
export function remainingInvestmentPool(
  basePurchase: number | null | undefined,
  _commission?: ShowroomChargeDraft | DealPartnerDraft | null,
  dealType?: ProductDealType | null,
  profitRuleType?: ProductProfitRuleType | null,
): number | null {
  if (!shouldAutoDistributePartnerInvestments(dealType, profitRuleType)) {
    return null;
  }
  const base = Number(basePurchase);
  if (!Number.isFinite(base) || base <= 0) return null;
  return Number(base.toFixed(2));
}

/**
 * Equally distribute remaining investment across deal partners.
 * Updates investmentAmount + investmentRatio for each row. Residual cents go to the last partner.
 */
export function distributePartnerInvestments(
  partners: DealPartnerDraft[] | undefined,
  remaining: number | null,
): DealPartnerDraft[] {
  const rows = addableDealPartners(partners);
  if (rows.length === 0) return [];

  if (remaining == null || !Number.isFinite(remaining) || remaining < 0) {
    return rows.map((r) => ({
      ...r,
      investmentAmount: null,
      investmentRatio: null,
    }));
  }

  if (remaining === 0) {
    return rows.map((r) => ({
      ...r,
      investmentAmount: 0,
      investmentRatio: 0,
    }));
  }

  const n = rows.length;
  const share = Number((remaining / n).toFixed(2));
  let allocated = 0;

  return rows.map((r, i) => {
    const amount =
      i === n - 1 ? Number((remaining - allocated).toFixed(2)) : share;
    allocated = Number((allocated + amount).toFixed(2));
    const ratio = Number(((amount / remaining) * 100).toFixed(4));
    return {
      ...r,
      investmentAmount: amount,
      investmentRatio: ratio,
    };
  });
}

/** True when partner investment rows already match the auto-distributed amounts. */
export function partnerInvestmentsMatchDistribution(
  partners: DealPartnerDraft[] | undefined,
  remaining: number | null,
): boolean {
  const expected = distributePartnerInvestments(partners, remaining);
  const current = addableDealPartners(partners);
  if (expected.length !== current.length) return false;
  return expected.every((row, i) => {
    const cur = current[i];
    if (!cur || cur.key !== row.key) return false;
    const a = cur.investmentAmount;
    const b = row.investmentAmount;
    if (a == null && b == null) return true;
    if (a == null || b == null) return false;
    return Math.abs(Number(a) - Number(b)) < 0.005;
  });
}

export function showsOwnerDemandPrice(rule: ProductProfitRuleType | null | undefined): boolean {
  return rule === "owner_surplus";
}

export function showsPartnerInvestmentAmount(_rule?: ProductProfitRuleType | null): boolean {
  return true;
}

/** Whether Deal Partners should show the Profit % column. */
export function showsPartnerProfitColumns(
  dealType: ProductDealType | null | undefined,
  profitRuleType: ProductProfitRuleType | null | undefined,
): boolean {
  if (dealType === "partnership") return true;
  if (dealType === "purchased") return false;
  if (dealType === "owner_vehicle") return profitRuleType === "profit_sharing";
  return false;
}

/**
 * Partnership deals: Showroom Charges (Fixed or Percentage) are fully independent of
 * Deal Partner Profit %. Neither side rewrites the other.
 */
export function isShowroomIndependentOfPartnerProfits(
  dealType: ProductDealType | null | undefined,
  _profitRuleType?: ProductProfitRuleType | null | undefined,
): boolean {
  return dealType === "partnership";
}

/**
 * Remaining profit % for deal partners after Showroom Charges.
 * - Sale % / Profit % showroom: 100 − showroom %
 * - Fixed showroom: ((Expected Sale Price − fixed) / Expected Sale Price) × 100
 * All profit percentages are stored/displayed to 2 decimal places.
 */
export function roundProfitPercent(value: number): number {
  return Number(Math.max(0, Math.min(100, value)).toFixed(2));
}

export function remainingPartnerProfitPercent(input: {
  showroom: ShowroomChargeDraft | null | undefined;
  expectedSalePrice?: number | null;
}): number | null {
  if (!hasShowroomCharge(input.showroom)) return null;
  const showroom = input.showroom!;
  const value = Number(showroom.commissionValue);
  if (!Number.isFinite(value) || value < 0) return null;

  if (isShowroomPercentType(showroom.commissionType)) {
    return roundProfitPercent(100 - Math.min(100, value));
  }

  if (showroom.commissionType === "fixed") {
    const esp = Number(input.expectedSalePrice);
    if (!Number.isFinite(esp) || esp <= 0) return null;
    const remaining = Math.max(0, esp - value);
    return roundProfitPercent((remaining / esp) * 100);
  }

  return null;
}

/** Sum of percentage profit values on deal partners that have a party selected. */
export function sumPartnerProfitPercents(partners: DealPartnerDraft[] | undefined): number {
  return addableDealPartners(partners).reduce((sum, row) => {
    if (!row.partyId) return sum;
    if (row.profitType !== "percentage" && row.profitType != null) return sum;
    const value = Number(row.profitValue);
    if (!Number.isFinite(value) || value < 0) return sum;
    return sum + value;
  }, 0);
}

export function partnersHaveAnyProfitPercent(partners: DealPartnerDraft[] | undefined): boolean {
  return addableDealPartners(partners).some((row) => {
    if (!row.partyId) return false;
    if (row.profitType !== "percentage" && row.profitType != null) return false;
    return row.profitValue != null && Number.isFinite(Number(row.profitValue));
  });
}

/** Clear Profit % on all deal partners (keeps party / investment). */
export function clearPartnerProfitPercents(
  partners: DealPartnerDraft[] | undefined,
): DealPartnerDraft[] {
  return addableDealPartners(partners).map((row) => {
    if (row.profitValue == null && (row.profitType === "percentage" || row.profitType == null)) {
      return row;
    }
    return {
      ...row,
      profitType: "percentage" as const,
      profitValue: null,
    };
  });
}

/** Clear Showroom Charges value while preserving type when possible. */
export function clearShowroomChargeValue(
  showroom: ShowroomChargeDraft | null | undefined,
): ShowroomChargeDraft {
  const prev = showroom ?? emptyShowroomCharge();
  return emptyShowroomCharge({
    partyId: prev.partyId,
    partyName: prev.partyName,
    commissionType: prev.commissionType,
    commissionValue: null,
    commissionAmount: null,
  });
}

/** @deprecated Use clearShowroomChargeValue */
export function clearShowroomPercentCharge(
  showroom: ShowroomChargeDraft | null | undefined,
): ShowroomChargeDraft {
  return clearShowroomChargeValue(showroom);
}

/**
 * Clamp a partner Profit % edit so partner percentages never exceed 100% together.
 * Other partners keep their current %, so this row's max is 100 − others.
 */
export function clampPartnerProfitPercentEdit(
  partners: DealPartnerDraft[] | undefined,
  index: number,
  nextValue: number | null,
): number | null {
  if (nextValue == null || !Number.isFinite(nextValue)) return null;
  const rows = addableDealPartners(partners);
  const others = rows.reduce((sum, row, i) => {
    if (i === index || !row.partyId) return sum;
    if (row.profitType !== "percentage" && row.profitType != null) return sum;
    const value = Number(row.profitValue);
    if (!Number.isFinite(value) || value < 0) return sum;
    return sum + value;
  }, 0);
  const max = roundProfitPercent(100 - others);
  return roundProfitPercent(Math.max(0, Math.min(max, nextValue)));
}

/** Showroom % remaining after partner profit percentages (0–100). */
export function showroomPercentFromPartnerProfits(
  partners: DealPartnerDraft[] | undefined,
): number {
  const partnerSum = sumPartnerProfitPercents(partners);
  return roundProfitPercent(100 - partnerSum);
}

/**
 * Fixed showroom amount implied by partner profit %:
 * Expected Sale Price × (100 − partner %) / 100
 */
export function showroomFixedFromPartnerProfits(
  partners: DealPartnerDraft[] | undefined,
  expectedSalePrice?: number | null,
): number | null {
  const esp = Number(expectedSalePrice);
  if (!Number.isFinite(esp) || esp <= 0) return null;
  const partnerPct = Math.max(0, Math.min(100, sumPartnerProfitPercents(partners)));
  return Number(((esp * (100 - partnerPct)) / 100).toFixed(2));
}

/**
 * Sync Showroom Charges from deal partner Profit %.
 * - Sale % / Profit % showroom: commission value = 100 − partner %
 * - Fixed showroom: commission value = Expected Sale × (100 − partner %) / 100
 *   (preserves the entered fixed amount when reverse-calc is only rounding noise)
 * - Cleared partners: clear showroom value (keep type)
 */
export function applyShowroomChargeFromPartners(input: {
  showroom: ShowroomChargeDraft | null | undefined;
  partners: DealPartnerDraft[] | undefined;
  expectedSalePrice?: number | null;
}): ShowroomChargeDraft {
  const prev = input.showroom ?? emptyShowroomCharge();
  if (!partnersHaveAnyProfitPercent(input.partners)) {
    return clearShowroomChargeValue(prev);
  }

  if (prev.commissionType === "fixed") {
    const current =
      prev.commissionValue != null && Number.isFinite(Number(prev.commissionValue))
        ? Number(prev.commissionValue)
        : null;

    // If partner % still matches what this fixed amount implies (at 2 dp),
    // keep the exact entered fixed amount — do not reverse-calc.
    if (current != null && input.expectedSalePrice != null) {
      const impliedPartnerPct = remainingPartnerProfitPercent({
        showroom: prev,
        expectedSalePrice: input.expectedSalePrice,
      });
      const actualPartnerPct = roundProfitPercent(sumPartnerProfitPercents(input.partners));
      if (
        impliedPartnerPct != null &&
        Math.abs(impliedPartnerPct - actualPartnerPct) < 0.005
      ) {
        return prev;
      }
    }

    const fixed = showroomFixedFromPartnerProfits(input.partners, input.expectedSalePrice);
    if (fixed == null) return prev;
    return emptyShowroomCharge({
      partyId: prev.partyId,
      partyName: prev.partyName,
      commissionType: "fixed",
      commissionValue: fixed,
      commissionAmount: fixed,
    });
  }

  const commissionValue = showroomPercentFromPartnerProfits(input.partners);
  const percentType: ShowroomChargeType =
    prev.commissionType === "sale_percentage" || prev.commissionType === "profit_percentage"
      ? prev.commissionType
      : "profit_percentage";
  return emptyShowroomCharge({
    partyId: prev.partyId,
    partyName: prev.partyName,
    commissionType: percentType,
    commissionValue,
    commissionAmount: prev.commissionAmount,
  });
}

/** Signature of partner profit % values — used to detect real Profit % edits vs party-only changes. */
export function partnerProfitSignature(partners: DealPartnerDraft[] | undefined): string {
  return addableDealPartners(partners)
    .map((p) => `${p.profitType ?? ""}:${p.profitValue ?? ""}`)
    .join("|");
}

/** @deprecated Use applyShowroomChargeFromPartners */
export function applyShowroomPercentFromPartners(
  showroom: ShowroomChargeDraft | null | undefined,
  partners: DealPartnerDraft[] | undefined,
): ShowroomChargeDraft {
  return applyShowroomChargeFromPartners({ showroom, partners });
}

export function showroomMatchesPartnerAllocation(input: {
  showroom: ShowroomChargeDraft | null | undefined;
  partners: DealPartnerDraft[] | undefined;
  expectedSalePrice?: number | null;
}): boolean {
  const showroom = input.showroom;
  if (!partnersHaveAnyProfitPercent(input.partners)) {
    return showroom?.commissionValue == null || !Number.isFinite(Number(showroom.commissionValue));
  }

  if (showroom?.commissionType === "fixed") {
    if (showroom.commissionValue == null || !Number.isFinite(Number(showroom.commissionValue))) {
      return false;
    }
    // In sync when partner % matches what the fixed amount implies at 2 dp.
    const impliedPartnerPct = remainingPartnerProfitPercent({
      showroom,
      expectedSalePrice: input.expectedSalePrice,
    });
    if (impliedPartnerPct == null) return true;
    const actualPartnerPct = roundProfitPercent(sumPartnerProfitPercents(input.partners));
    return Math.abs(impliedPartnerPct - actualPartnerPct) < 0.005;
  }

  if (!isShowroomPercentType(showroom?.commissionType)) return false;
  const expectedPct = showroomPercentFromPartnerProfits(input.partners);
  if (showroom.commissionValue == null || !Number.isFinite(Number(showroom.commissionValue))) {
    return false;
  }
  return Math.abs(Number(showroom.commissionValue) - expectedPct) < 0.005;
}

/** @deprecated Use showroomMatchesPartnerAllocation */
export function showroomMatchesPartnerRemaining(
  showroom: ShowroomChargeDraft | null | undefined,
  partners: DealPartnerDraft[] | undefined,
): boolean {
  return showroomMatchesPartnerAllocation({ showroom, partners });
}

/**
 * Assign remaining profit % equally across deal partners that have a party selected.
 * Residual cents go to the last selected partner.
 */
export function applyRemainingProfitPercentToPartners(
  partners: DealPartnerDraft[] | undefined,
  remainingPercent: number | null,
): DealPartnerDraft[] {
  const rows = addableDealPartners(partners);
  if (remainingPercent == null || !Number.isFinite(remainingPercent)) return rows;

  const selectedIndexes = rows
    .map((row, index) => (row.partyId ? index : -1))
    .filter((index) => index >= 0);
  if (selectedIndexes.length === 0) return rows;

  const n = selectedIndexes.length;
  const share = roundProfitPercent(remainingPercent / n);
  let allocated = 0;

  return rows.map((row, index) => {
    const selectedPos = selectedIndexes.indexOf(index);
    if (selectedPos < 0) return row;
    const profitValue =
      selectedPos === n - 1
        ? roundProfitPercent(remainingPercent - allocated)
        : share;
    if (selectedPos !== n - 1) {
      allocated = Number((allocated + profitValue).toFixed(2));
    }
    if (
      row.profitType === "percentage" &&
      row.profitValue != null &&
      Math.abs(Number(row.profitValue) - profitValue) < 0.005
    ) {
      return row;
    }
    return {
      ...row,
      profitType: "percentage" as const,
      profitValue,
    };
  });
}

export function partnerProfitsMatchRemaining(
  partners: DealPartnerDraft[] | undefined,
  remainingPercent: number | null,
): boolean {
  const expected = applyRemainingProfitPercentToPartners(partners, remainingPercent);
  const current = addableDealPartners(partners);
  if (expected.length !== current.length) return false;
  return expected.every((row, i) => {
    const cur = current[i];
    if (!cur || cur.key !== row.key) return false;
    if (!cur.partyId) return true;
    if (cur.profitType !== "percentage") return false;
    if (row.profitValue == null || cur.profitValue == null) return false;
    return Math.abs(Number(cur.profitValue) - Number(row.profitValue)) < 0.005;
  });
}

/** Investment ratio is always shown when partners have amounts (calculated, optional). */
export function showsCalculatedInvestmentRatio(
  _rule?: ProductProfitRuleType | null,
  _dealType?: ProductDealType | null,
): boolean {
  return true;
}

export function investmentAmountTotal(partners: DealPartnerDraft[]): number {
  return partners.reduce((sum, p) => sum + (Number(p.investmentAmount) || 0), 0);
}

export function calculatedInvestmentRatio(amount: number | null | undefined, total: number): number | null {
  if (total <= 0 || amount == null || !Number.isFinite(Number(amount))) return null;
  return (Number(amount) / total) * 100;
}

/** Round investment ratio for storage/display (4 dp, clamped 0–100). */
export function roundInvestmentRatio(value: number): number {
  return Number(Math.max(0, Math.min(100, value)).toFixed(4));
}

/**
 * Recalculate every partner's Investment Ratio from their amounts vs Base Purchase Cost.
 * Ratio = amount / Base Purchase Cost × 100 (falls back to sum of amounts only when base is unset).
 */
export function withRecalculatedInvestmentRatios(
  partners: DealPartnerDraft[] | undefined,
  basePurchaseCost?: number | null,
): DealPartnerDraft[] {
  const rows = addableDealPartners(partners);
  const base = Number(basePurchaseCost);
  const denominator =
    Number.isFinite(base) && base > 0 ? base : investmentAmountTotal(rows);
  return rows.map((row) => {
    const ratio = calculatedInvestmentRatio(row.investmentAmount, denominator);
    return {
      ...row,
      investmentRatio: ratio == null ? null : roundInvestmentRatio(ratio),
    };
  });
}

/**
 * Clamp one partner's Investment Amount so the group never exceeds `maxTotal`
 * (Base Purchase Cost). Other partners keep their current amounts.
 */
export function clampPartnerInvestmentAmountEdit(
  partners: DealPartnerDraft[] | undefined,
  index: number,
  nextAmount: number | null,
  maxTotal: number | null | undefined,
): number | null {
  if (nextAmount == null || !Number.isFinite(nextAmount)) return null;
  let amount = Math.max(0, Number(nextAmount));
  const rows = addableDealPartners(partners);
  const cap = Number(maxTotal);
  if (Number.isFinite(cap) && cap >= 0) {
    const others = rows.reduce((sum, row, i) => {
      if (i === index) return sum;
      return sum + (Number(row.investmentAmount) || 0);
    }, 0);
    const maxForRow = Math.max(0, Number((cap - others).toFixed(2)));
    amount = Math.min(amount, maxForRow);
  }
  return Number(amount.toFixed(2));
}

/**
 * Apply an Investment Amount edit: clamp to Base Purchase Cost, then refresh all ratios vs base.
 */
export function applyPartnerInvestmentAmountEdit(
  partners: DealPartnerDraft[] | undefined,
  index: number,
  nextAmount: number | null,
  maxTotal: number | null | undefined,
): DealPartnerDraft[] {
  const rows = addableDealPartners(partners);
  const clamped = clampPartnerInvestmentAmountEdit(rows, index, nextAmount, maxTotal);
  const next = rows.map((row, i) => (i === index ? { ...row, investmentAmount: clamped } : row));
  return withRecalculatedInvestmentRatios(next, maxTotal);
}

/**
 * Apply an Investment Ratio edit: compute implied Investment Amount = base * (ratio / 100), then refresh ratios vs base.
 */
export function applyPartnerInvestmentRatioEdit(
  partners: DealPartnerDraft[] | undefined,
  index: number,
  nextRatio: number | null,
  maxTotal: number | null | undefined,
): DealPartnerDraft[] {
  const rows = addableDealPartners(partners);
  const cap = Number(maxTotal);
  const totalInvested = investmentAmountTotal(rows);
  const denominator = Number.isFinite(cap) && cap > 0 ? cap : totalInvested;

  if (nextRatio == null || !Number.isFinite(nextRatio) || denominator <= 0) {
    const next = rows.map((row, i) => (i === index ? { ...row, investmentRatio: null } : row));
    return next;
  }

  const clampedRatio = Math.max(0, Math.min(100, Number(nextRatio)));
  const calculatedAmount = Number(((denominator * clampedRatio) / 100).toFixed(2));
  const clampedAmount = clampPartnerInvestmentAmountEdit(rows, index, calculatedAmount, maxTotal);

  const next = rows.map((row, i) =>
    i === index
      ? { ...row, investmentAmount: clampedAmount, investmentRatio: clampedRatio }
      : row,
  );
  return withRecalculatedInvestmentRatios(next, maxTotal);
}

export function validateDealDraft(input: {
  dealType: ProductDealType | null | undefined;
  profitRuleType: ProductProfitRuleType | null | undefined;
  ownerDemandPrice: number | null | undefined;
  dealPartners: DealPartnerDraft[];
  showroomCharge?: ShowroomChargeDraft | null;
  /** Base Purchase Cost — partner investments must not exceed this when set. */
  basePurchaseCost?: number | null;
  /** @deprecated */
  companyOwnerCommission?: ShowroomChargeDraft | null;
}): string | null {
  const dealType = input.dealType ?? null;
  const rule = input.profitRuleType ?? null;
  const partners = addableDealPartners(input.dealPartners);
  const showroom =
    input.showroomCharge ?? input.companyOwnerCommission ?? emptyShowroomCharge();

  if (dealType === "purchased") {
    if (rule != null) {
      return "Purchased does not use a Profit Rule.";
    }
  }

  if (dealType && rule) {
    const allowed = profitRulesForDealType(dealType);
    if (!allowed.includes(rule)) {
      if (dealType === "owner_vehicle") {
        return "Owner Vehicle only allows Owner Surplus or Commission.";
      }
      return "Partnership only allows Profit Sharing.";
    }
  }

  if (rule === "owner_surplus") {
    if (input.ownerDemandPrice == null || !Number.isFinite(Number(input.ownerDemandPrice))) {
      return "Owner Demand Price is required when Profit Rule is Owner Surplus.";
    }
    if (partners.filter((p) => p.partyId).length !== 1) {
      return "Owner Surplus requires exactly one deal partner.";
    }
  }

  // Charge type is always pre-selected; an empty value simply means no showroom charge.
  if (
    (showroom.commissionType === "fixed" || isShowroomPercentType(showroom.commissionType)) &&
    showroom.commissionValue != null &&
    Number.isFinite(Number(showroom.commissionValue))
  ) {
    if (isShowroomPercentType(showroom.commissionType) && Number(showroom.commissionValue) > 100) {
      return "Percentage commission cannot exceed 100.";
    }
    if (Number(showroom.commissionValue) < 0) {
      return "Commission value cannot be negative.";
    }
    // company_id comes from the logged-in user (no picker); backend also defaults it.
  }

  const showPartnerProfit = showsPartnerProfitColumns(dealType, rule);
  const showroomIndependent = isShowroomIndependentOfPartnerProfits(dealType, rule);

  if (
    showPartnerProfit &&
    !showroomIndependent &&
    isShowroomPercentType(showroom.commissionType) &&
    showroom.commissionValue != null &&
    Number.isFinite(Number(showroom.commissionValue))
  ) {
    const partnerSum = sumPartnerProfitPercents(partners);
    const total = partnerSum + Number(showroom.commissionValue);
    if (total > 100.0001) {
      return "Showroom Charges % and Deal Partner Profit % together cannot exceed 100.";
    }
  }

  if (showPartnerProfit && showroomIndependent) {
    const partnerSum = sumPartnerProfitPercents(partners);
    if (partnerSum > 100.0001) {
      return "Deal Partner Profit % together cannot exceed 100.";
    }
  }

  for (const p of partners.filter((row) => row.partyId)) {
    if (!showPartnerProfit) continue;
    if (p.profitType === "percentage" || p.profitType === "fixed") {
      if (p.profitValue == null || !Number.isFinite(Number(p.profitValue))) {
        return "Profit Value is required when Profit Type is Fixed or Percentage.";
      }
      if (p.profitType === "percentage" && Number(p.profitValue) > 100) {
        return "Percentage profit value cannot exceed 100.";
      }
      if (Number(p.profitValue) < 0) {
        return "Profit Value cannot be negative.";
      }
    }
  }

  for (const p of partners) {
    if (!p.partyId) {
      if (p.investmentAmount != null || p.profitType != null || p.profitValue != null) {
        return "Select a Party for each partner row.";
      }
    }
  }

  const investTotal = investmentAmountTotal(partners);
  const baseCap = Number(input.basePurchaseCost);
  if (Number.isFinite(baseCap) && baseCap >= 0 && investTotal > baseCap + 0.009) {
    return "Total Investment Amount of all partners cannot exceed Base Purchase Cost.";
  }
  if (investTotal > 0) {
    const ratioBase =
      Number.isFinite(baseCap) && baseCap > 0 ? baseCap : investTotal;
    const ratios = partners.map(
      (p) => calculatedInvestmentRatio(p.investmentAmount, ratioBase) ?? 0,
    );
    const ratioSum = ratios.reduce((a, b) => a + b, 0);
    if (ratioSum > 100.01) {
      return "Total Investment Ratio (%) cannot exceed 100.";
    }
  }

  return null;
}

export function serializeDealPartnersForApi(
  partners: DealPartnerDraft[] | undefined,
  _profitRuleType?: ProductProfitRuleType | null,
  _dealType?: ProductDealType | null,
  basePurchaseCost?: number | null,
) {
  const addable = addableDealPartners(partners).filter((p) => Boolean(p.partyId));
  const invested = investmentAmountTotal(addable);
  const base = Number(basePurchaseCost);
  const denominator = Number.isFinite(base) && base > 0 ? base : invested;

  return addable.map((p) => {
    const ratio =
      denominator > 0 && p.investmentAmount != null
        ? roundInvestmentRatio((Number(p.investmentAmount) / denominator) * 100)
        : null;

    const profitValue =
      p.profitType === "percentage" && p.profitValue != null && Number.isFinite(Number(p.profitValue))
        ? roundProfitPercent(Number(p.profitValue))
        : p.profitValue;

    return {
      party_id: p.partyId != null && String(p.partyId) !== "" ? Number(p.partyId) : null,
      investment_amount: p.investmentAmount,
      investment_ratio: ratio,
      profit_type: p.profitType,
      profit_value: profitValue,
    };
  });
}

export function serializeShowroomChargeForApi(
  commission: ShowroomChargeDraft | null | undefined,
  _basePurchase?: number | null,
  fallbackPartyId?: string | number | null,
): Record<string, unknown> | null {
  const row = commission ?? emptyShowroomCharge();
  if (!hasShowroomCharge(row)) {
    return null;
  }
  const partyId =
    row.partyId != null && String(row.partyId).trim() !== ""
      ? Number(row.partyId)
      : fallbackPartyId != null && String(fallbackPartyId).trim() !== ""
        ? Number(fallbackPartyId)
        : null;
  if (partyId == null || !Number.isFinite(partyId) || partyId <= 0) {
    return null;
  }
  return {
    party_id: partyId,
    commission_type: row.commissionType,
    commission_value:
      isShowroomPercentType(row.commissionType) && row.commissionValue != null
        ? roundProfitPercent(Number(row.commissionValue))
        : row.commissionValue,
  };
}

export type MappedDealPartners = {
  dealPartners: DealPartnerDraft[];
};

export function mapDealPartnersFromApi(raw: unknown): MappedDealPartners {
  if (!Array.isArray(raw)) {
    return { dealPartners: [] };
  }

  const mapped = raw.map((row, i) => {
    const r = row && typeof row === "object" ? (row as Record<string, unknown>) : {};
    const partyId = r.party_id ?? r.partyId;
    const profitTypeRaw = r.profit_type ?? r.profitType;
    const profitType: DealPartnerProfitType | null =
      profitTypeRaw === "percentage" || profitTypeRaw === "fixed" ? profitTypeRaw : null;
    const amount = r.investment_amount ?? r.investmentAmount;
    const ratio = r.investment_ratio ?? r.investmentRatio;
    const value = r.profit_value ?? r.profitValue;
    const partyNameRaw = r.party_name ?? r.partyName;
    const partyName =
      partyNameRaw != null && String(partyNameRaw).trim() !== "" ? String(partyNameRaw).trim() : null;
    const isCompanyOwner = r.is_company_owner === true;

    return {
      key: isCompanyOwner
        ? COMPANY_OWNER_PARTNER_KEY
        : `dp-api-${r.id ?? i}-${Math.random().toString(36).slice(2, 6)}`,
      partyId: partyId != null && String(partyId) !== "" ? String(partyId) : null,
      partyName: isCompanyOwner ? COMPANY_OWNER_PARTY_NAME : partyName,
      investmentAmount:
        amount == null || amount === "" ? null : Number.isFinite(Number(amount)) ? Number(amount) : null,
      investmentRatio:
        ratio == null || ratio === "" ? null : Number.isFinite(Number(ratio)) ? Number(ratio) : null,
      profitType,
      profitValue:
        value == null || value === ""
          ? null
          : Number.isFinite(Number(value))
            ? profitType === "percentage"
              ? roundProfitPercent(Number(value))
              : Number(value)
            : null,
      isCompanyOwner,
    } satisfies DealPartnerDraft;
  });

  return {
    dealPartners: addableDealPartners(mapped),
  };
}

export function mapShowroomChargeFromApi(raw: unknown): ShowroomChargeDraft {
  if (!raw || typeof raw !== "object") {
    return emptyShowroomCharge();
  }
  const r = raw as Record<string, unknown>;
  const typeRaw = r.commission_type ?? r.commissionType;
  const commissionType = normalizeShowroomChargeType(typeRaw);
  const partyId = r.party_id ?? r.partyId ?? r.company_id ?? r.companyId;
  const partyNameRaw = r.party_name ?? r.partyName ?? r.company_name ?? r.companyName;
  const value = r.commission_value ?? r.commissionValue;
  const amount = r.commission_amount ?? r.commissionAmount;

  return emptyShowroomCharge({
    partyId: partyId != null && String(partyId) !== "" ? String(partyId) : null,
    partyName:
      partyNameRaw != null && String(partyNameRaw).trim() !== ""
        ? String(partyNameRaw).trim()
        : null,
    commissionType,
    commissionValue:
      value != null && Number.isFinite(Number(value))
        ? isShowroomPercentType(commissionType)
          ? roundProfitPercent(Number(value))
          : Number(value)
        : null,
    commissionAmount: amount != null && Number.isFinite(Number(amount)) ? Number(amount) : null,
  });
}
